import { useEffect, useRef, useState } from 'react'
import { Package } from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { useAuthStore } from '../../stores/authStore'
import { landingFor } from '../../lib/landing'
import { useDisplayPrefs } from '../../lib/display-prefs'
import { effectiveDisplay } from '../../lib/display-prefs-core'

/**
 * Customer "Our Products" page — embeds the shared products feed
 * (themasterdashboard.com) per add-our-products.md:
 *   • §4  iframe with the user's email (feed filters public vs in-house by
 *         email DOMAIN) + optional company; header=none/filters=none so the
 *         feed drops its own title + search (our page supplies the heading).
 *   • §6  the frame is cross-origin and can't read our stylesheet, so we TELL
 *         it our theme + brand accent two ways: on the URL (first paint) and
 *         via postMessage (repaint on toggle without reloading). Read from the
 *         app's theme store — never prefers-color-scheme.
 *
 * Everything is read from the authenticated session / theme store each render;
 * nothing is hard-coded.
 */
const EMBED_KEY = 'ph_live_8a2ad56d365ef480610761682b243ce2e5fd'
const EMBED_ORIGIN = 'https://themasterdashboard.com'
const FRAME_ID = 'our-products'

/** Current app accent as 6 hex digits (no #) — the feed's only hue (§6c). */
function readAccentHex() {
  try {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-accent').trim()
    const hex = raw.replace('#', '')
    if (/^[0-9a-fA-F]{6}$/.test(hex)) return hex.toLowerCase()
    const m = raw.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
    if (m) return [1, 2, 3].map((i) => Number(m[i]).toString(16).padStart(2, '0')).join('')
  } catch { /* fall through to brand default */ }
  return '02367b' // Deep Navy — brand accent
}

export function OurProductsPage() {
  // Breadcrumb Home is role-aware (super_admin → /super-admin/dashboard, admins
  // → /dashboard, users → /app), all resolved by landingFor().
  const role = useAuthStore((s) => s.user?.role)
  const emailId = useAuthStore((s) => s.user?.emailId)
  const tenantId = useAuthStore((s) => s.user?.tenantId)

  // Theme from the APP's store (never prefers-color-scheme, §6). 'system' is
  // resolved to the concrete light/dark actually on screen.
  const { effective, system } = useDisplayPrefs()
  const theme = effectiveDisplay(effective, system).theme // 'light' | 'dark', reactive

  const frameRef = useRef(null)
  const [frameHeight, setFrameHeight] = useState('70vh')

  // §6a: put the theme/accent in the URL for a correct FIRST paint. We freeze
  // them at mount so a later theme toggle repaints via postMessage (§6b)
  // instead of reloading the iframe (a reactive key={theme} would reload on
  // every toggle, which §6b explicitly avoids).
  const initialRef = useRef(null)
  if (initialRef.current === null) initialRef.current = { theme, accent: readAccentHex() }

  // §4: size the iframe to fill the shell's content area (never 100vh — that
  // double-scrolls); subtract the shell's bottom padding.
  useEffect(() => {
    const measure = () => {
      const el = frameRef.current
      if (!el) return
      const top = el.getBoundingClientRect().top
      setFrameHeight(`${Math.max(320, Math.round(window.innerHeight - top - 40))}px`)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  // §6b: push theme + accent to the cross-origin feed. It re-announces
  // md-products-ready until answered, so respond to that AND send immediately
  // whenever the app theme changes — repainting the feed without a reload.
  useEffect(() => {
    const send = () => {
      const w = frameRef.current?.contentWindow
      // Target the feed's origin explicitly (never '*'): the message is then only
      // delivered while the frame really holds EMBED_ORIGIN, mirroring the
      // e.origin check on the inbound side below.
      if (w) w.postMessage({ type: 'md-products-theme', theme, accent: readAccentHex() }, EMBED_ORIGIN)
    }
    const onMessage = (e) => {
      if (e.origin === EMBED_ORIGIN && e.data && e.data.type === 'md-products-ready') send()
    }
    window.addEventListener('message', onMessage)
    send()
    return () => window.removeEventListener('message', onMessage)
  }, [theme])

  const params = new URLSearchParams({
    key: EMBED_KEY,
    variant: 'page',
    header: 'none', // §4: our page supplies the heading — no second title
    filters: 'none', // §4: remove the feed's search + category chips
    theme: initialRef.current.theme, // §6a: correct first paint
    accent: initialRef.current.accent, // §6c: brand hue
  })
  if (emailId) params.set('email', emailId)
  if (tenantId) params.set('company', tenantId)
  const src = `${EMBED_ORIGIN}/embed/products?${params.toString()}`

  return (
    <div>
      <Breadcrumb
        current="Our Products"
        homePath={landingFor(role)}
        homeLabel="Dashboard"
      />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <Package size={22} className="text-[color:var(--color-accent-strong)]" />
          Our Products
        </h1>
        <p className="mt-1 text-[color:var(--color-ink-muted)]">
          Discover more products from our company. Open the ones you already
          have, or explore something new.
        </p>
      </div>

      <iframe
        id={FRAME_ID}
        ref={frameRef}
        src={src}
        title="Our Products"
        loading="lazy"
        style={{ width: '100%', height: frameHeight, border: 0, display: 'block' }}
      />
    </div>
  )
}
